import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, query, orderByChild, limitToFirst, startAt, get, set, update, endAt, limitToLast, equalTo } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SmartInboxEmail from './SmartInboxEmail';
import EmailDraftsPreview from './EmailDraftsPreview';
import EmailAiPopup from './EmailAiPopup';
import DraftBulkMode from './EmailAiModes/DraftBulkMode';
import ScheduledEmails from './ScheduledEmails';
import InboxFilters from './InboxFilters';
import './SmartInbox.css';

const SmartInbox = ({ userId }) => {
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [repliedEmails, setRepliedEmails] = useState([]);
  const [forwardedEmails, setForwardedEmails] = useState([]);
  const [deletedEmails, setDeletedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [inboxActive, setInboxActive] = useState(false);
  
  // NEW: Pagination states
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);
  const [lastEmailKey, setLastEmailKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [prefetchedNextPage, setPrefetchedNextPage] = useState(null); // NEW: Prefetching
  const PAGE_SIZE = 15; // Load 15 emails per page
  
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    spam: 0,
    sent: 0,
    scheduled: 0,
    replied: 0,
    deleted: 0
  });
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [previousAccount, setPreviousAccount] = useState('all'); // Track previous account to detect real changes
  const [enthusiasmFilter, setEnthusiasmFilter] = useState('all'); // 'all' or 'enthusiastic' (>=5)
  const [enthusiasmStats, setEnthusiasmStats] = useState({
    low: 0,    // 1-3
    medium: 0, // 4-6
    high: 0,   // 7-10
    unknown: 0 // not yet analyzed
  });
  const [showEnthusiasmDistribution, setShowEnthusiasmDistribution] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [autoProcessEmails, setAutoProcessEmails] = useState(false);
  const [selectedTag, setSelectedTag] = useState('all');
  const [aiTags, setAiTags] = useState([]);

  // Draft EmailAiPopup States
  const [showDraftEmailPopup, setShowDraftEmailPopup] = useState(false);
  const [draftEmailData, setDraftEmailData] = useState(null);

  // Draft Bulk Mode States
  const [showDraftBulkMode, setShowDraftBulkMode] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState([]);
  const [selectedDraftIndex, setSelectedDraftIndex] = useState(0);
  const [userSettings, setUserSettings] = useState({});
  const [aiSettings, setAiSettings] = useState({});
  const [userTopics, setUserTopics] = useState({});
  const [isTopicsLoaded, setIsTopicsLoaded] = useState(false);
  const [senderEmails, setSenderEmails] = useState([]);
  const [topicTypeMapping, setTopicTypeMapping] = useState({});

  const functions = getFunctions();
  const manualFetchEmails = httpsCallable(functions, 'manualFetchSmartDomainEmails');
  const analyzeEmailEndpoint = httpsCallable(functions, 'analyzeEmailEndpoint');
  const processEmailWithAgents = httpsCallable(functions, 'processEmailWithAgents');

  // 🚀 OPTIMIZED: Memoized calculations for better performance
  const enthusiasmStatsCalculated = useMemo(() => {
    if (!filteredEmails || filteredEmails.length === 0) {
      return { low: 0, medium: 0, high: 0, unknown: 0 };
    }

    let low = 0, medium = 0, high = 0, unknown = 0;

    filteredEmails.forEach(email => {
      if (email.enthusiasmScore === undefined) {
        unknown++;
      } else if (email.enthusiasmScore >= 1 && email.enthusiasmScore <= 3) {
        low++;
      } else if (email.enthusiasmScore >= 4 && email.enthusiasmScore <= 6) {
        medium++;
      } else if (email.enthusiasmScore >= 7 && email.enthusiasmScore <= 10) {
        high++;
      } else {
        unknown++;
      }
    });

    return { low, medium, high, unknown };
  }, [filteredEmails]);

  // 🚀 OPTIMIZED: Memoized email formatting
  const formatEmailAddress = useCallback((email) => {
    if (!email) return '';
    if (email.length <= 15) return email;
    
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    
    const username = parts[0];
    const domain = parts[1];
    
    const shortUsername = username.length > 8 
      ? username.substring(0, 6) + '...' 
      : username;
    
    return `${shortUsername}@${domain}`;
  }, []);

  // NEW: 🚀 SUPER-OPTIMIZED email loading with prefetching AND account filtering
  const loadEmailsPage = useCallback(async (pageNumber = 1, startAfterKey = null, isPrefetch = false, targetAccount = null) => {
    if (!userId) return null;

    // Use current selectedAccount if no targetAccount specified
    const accountToLoad = targetAccount || selectedAccount;

    try {
      const isInitialLoad = pageNumber === 1;
      
      if (!isPrefetch) {
        if (isInitialLoad) {
          setLoading(true);
          setEmails([]);
          setLastEmailKey(null);
          setCurrentPage(1);
          setHasMoreEmails(true);
        } else {
          setLoadingMore(true);
        }
      }

      console.log(`🚀 ${isPrefetch ? 'PREFETCHING' : 'LOADING'} emails page ${pageNumber} for account: ${accountToLoad}${startAfterKey ? ` starting after ${startAfterKey}` : ''}`);

      // 🚀 OPTIMIZED: Account-specific queries for MASSIVE Firebase savings
      let inboxQuery, spamQuery;
      
      if (accountToLoad === 'all') {
        // 🐛 FIXED: For "all" account, load more emails to account for deleted emails being filtered out
        // Many emails might be marked as deleted=true and filtered out in processEmailSnapshot
        const allAccountQueryLimit = startAfterKey ? (PAGE_SIZE + 1) * 3 : (PAGE_SIZE + 1) * 2;
        
        if (startAfterKey) {
          inboxQuery = query(
            ref(database, `/World/${userId}/inbox`),
            orderByChild('timestamp'),
            endAt(startAfterKey - 1),
            limitToLast(allAccountQueryLimit)
          );
          spamQuery = query(
            ref(database, `/World/${userId}/spam`),
            orderByChild('timestamp'),
            endAt(startAfterKey - 1),
            limitToLast(allAccountQueryLimit)
          );
        } else {
          inboxQuery = query(
            ref(database, `/World/${userId}/inbox`),
            orderByChild('timestamp'),
            limitToLast(allAccountQueryLimit)
          );
          spamQuery = query(
            ref(database, `/World/${userId}/spam`),
            orderByChild('timestamp'),
            limitToLast(allAccountQueryLimit)
          );
        }
      } else {
        // 🎯 TARGETED: Load only emails from specific account (HUGE savings!)
        console.log(`🎯 TARGETED QUERY: Loading only emails for account ${accountToLoad}`);
        
        // 🐛 FIXED: For account-specific queries, we need to load MORE emails initially
        // since we'll be filtering by timestamp later. Load more to ensure we have enough.
        const queryLimit = startAfterKey ? (PAGE_SIZE + 1) * 3 : (PAGE_SIZE + 1) * 2;
        
        inboxQuery = query(
          ref(database, `/World/${userId}/inbox`),
          orderByChild('accountId'),
          equalTo(accountToLoad),
          limitToLast(queryLimit)
        );
        spamQuery = query(
          ref(database, `/World/${userId}/spam`),
          orderByChild('accountId'),
          equalTo(accountToLoad),
          limitToLast(queryLimit)
        );
      }

      const [inboxSnapshot, spamSnapshot] = await Promise.all([
        get(inboxQuery),
        get(spamQuery)
      ]);

      let newEmails = [];

      // 🚀 OPTIMIZED: Faster email processing
      let totalEmailsFromFirebase = 0;
      let deletedEmailsFiltered = 0;
      
      const processEmailSnapshot = (snapshot, isSpam) => {
        if (!snapshot.exists()) return;
        
        const data = snapshot.val();
        const entries = Object.entries(data);
        
        for (let i = 0; i < entries.length; i++) {
          const [id, email] = entries[i];
          totalEmailsFromFirebase++;
          if (email.deleted !== true) {
            newEmails.push({
              id,
              ...email,
              isSpam
            });
          } else {
            deletedEmailsFiltered++;
          }
        }
      };

      processEmailSnapshot(inboxSnapshot, false);
      processEmailSnapshot(spamSnapshot, true);
      
      // 🔍 DEBUG: Log filtering statistics
      if (accountToLoad === 'all') {
        console.log(`🔍 FIREBASE vs FILTERED: Got ${totalEmailsFromFirebase} emails from Firebase, filtered out ${deletedEmailsFiltered} deleted emails, ${newEmails.length} remaining`);
      }

      // Sort by timestamp (newest first) - IMPORTANT when filtering by account
      newEmails.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      // Handle pagination for account-specific queries
      if (!isInitialLoad && accountToLoad !== 'all') {
        // For account-specific queries, we need to handle pagination differently
        // since Firebase doesn't support combined orderBy + startAfter on different fields
        if (startAfterKey && newEmails.length > 0) {
          // Filter out emails that are newer than or equal to our start point
          newEmails = newEmails.filter(email => email.timestamp < startAfterKey);
        }
      } else if (!isInitialLoad && newEmails.length > 0 && newEmails[0].timestamp === startAfterKey) {
        // Remove duplicate from pagination overlap (for 'all' account queries)
        newEmails = newEmails.slice(1);
      }

      // 🐛 FIXED: Better hasMore calculation
      // Store original count before filtering/slicing
      const totalEmailsBeforeSlicing = newEmails.length;
      
             // Check for more emails - improved logic
       let hasMore;
       if (accountToLoad === 'all') {
         // For 'all' account: Traditional pagination logic
         hasMore = totalEmailsBeforeSlicing > PAGE_SIZE;
       } else {
         // For specific accounts: Check if we have enough emails after filtering
         // If we got a full page worth of emails, there might be more
         if (isInitialLoad) {
           // First page: Check if we have more than PAGE_SIZE emails after filtering
           hasMore = totalEmailsBeforeSlicing > PAGE_SIZE;
         } else {
           // Subsequent pages: If we have PAGE_SIZE or more emails after filtering,
           // there might be more. If less, we've reached the end.
           hasMore = totalEmailsBeforeSlicing >= PAGE_SIZE;
         }
       }
      
      if (totalEmailsBeforeSlicing > PAGE_SIZE) {
        newEmails = newEmails.slice(0, PAGE_SIZE);
      }

      const lastEmail = newEmails[newEmails.length - 1];
      const newLastKey = lastEmail ? lastEmail.timestamp : null;

      // 🔍 DEBUG: Enhanced logging for pagination troubleshooting
      if (accountToLoad === 'all') {
        console.log(`🔍 ALL ACCOUNT PAGINATION: Page ${pageNumber}, loaded ${totalEmailsBeforeSlicing}/${newEmails.length} emails, hasMore: ${hasMore}`);
      } else {
        console.log(`🔍 ACCOUNT-SPECIFIC PAGINATION: Page ${pageNumber} for ${accountToLoad}, loaded ${totalEmailsBeforeSlicing}/${newEmails.length} emails, hasMore: ${hasMore}, isInitialLoad: ${isInitialLoad}`);
      }
      
      console.log(`🚀 LOADED ${newEmails.length} emails for account ${accountToLoad}, hasMore: ${hasMore}`);

      if (isPrefetch) {
        // Store prefetched data
        setPrefetchedNextPage({
          emails: newEmails,
          hasMore,
          lastKey: newLastKey,
          pageNumber,
          account: accountToLoad
        });
        console.log(`🚀 PREFETCHED page ${pageNumber} for account ${accountToLoad} (${newEmails.length} emails)`);
        return { emails: newEmails, hasMore, lastKey: newLastKey };
      }

      // Use prefetched data if available AND for same account
      if (prefetchedNextPage && 
          prefetchedNextPage.pageNumber === pageNumber && 
          prefetchedNextPage.account === accountToLoad) {
        console.log(`🚀 USING PREFETCHED page ${pageNumber} for account ${accountToLoad}`);
        newEmails = prefetchedNextPage.emails;
        setHasMoreEmails(prefetchedNextPage.hasMore);
        setLastEmailKey(prefetchedNextPage.lastKey);
        setPrefetchedNextPage(null); // Clear used prefetch
      } else {
        setLastEmailKey(newLastKey);
        setHasMoreEmails(hasMore);
      }

      if (isInitialLoad) {
        setEmails(newEmails);
      } else {
        setEmails(prevEmails => [...prevEmails, ...newEmails]);
      }

      setCurrentPage(pageNumber);

      // 🚀 NEW: Prefetch next page automatically for instant loading (only for same account)
      if (hasMore && !isPrefetch && pageNumber <= 3) { // Only prefetch first few pages
        setTimeout(() => {
          loadEmailsPage(pageNumber + 1, newLastKey, true, accountToLoad);
        }, 100); // Small delay to not block UI
      }

      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }

      setError(null);
      return { emails: newEmails, hasMore, lastKey: newLastKey };

    } catch (error) {
      console.error('Error loading emails page:', error);
      if (!isPrefetch) {
        setError('Error loading emails: ' + error.message);
        setLoading(false);
        setLoadingMore(false);
      }
      return null;
    }
  }, [userId, selectedAccount, prefetchedNextPage]);

  // 🚀 OPTIMIZED: Super-fast load more with prefetching
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMoreEmails) return;
    
    // Use prefetched data if available AND for same account
    if (prefetchedNextPage && 
        prefetchedNextPage.pageNumber === currentPage + 1 && 
        prefetchedNextPage.account === selectedAccount) {
      console.log(`🚀 INSTANT LOAD from prefetch for account ${selectedAccount}!`);
      setEmails(prev => [...prev, ...prefetchedNextPage.emails]);
      setHasMoreEmails(prefetchedNextPage.hasMore);
      setLastEmailKey(prefetchedNextPage.lastKey);
      setCurrentPage(currentPage + 1);
      setPrefetchedNextPage(null);
      
      // Prefetch next page for same account
      if (prefetchedNextPage.hasMore) {
        setTimeout(() => {
          loadEmailsPage(currentPage + 2, prefetchedNextPage.lastKey, true, selectedAccount);
        }, 50);
      }
    } else {
      // Fallback to normal loading with current account
      loadEmailsPage(currentPage + 1, lastEmailKey, false, selectedAccount);
    }
  }, [loadingMore, hasMoreEmails, prefetchedNextPage, currentPage, lastEmailKey, selectedAccount, loadEmailsPage]);

  // NEW: 🚀 TURBO-FAST count loading with shallow queries
  const loadEmailCounts = useCallback(async (targetAccount = null) => {
    if (!userId) return;

    // Use current selectedAccount if no targetAccount specified
    const accountToCount = targetAccount || selectedAccount;

    try {
      console.log(`🚀 TURBO-LOADING email counts for account: ${accountToCount}...`);
      
      let countPromises;
      
      if (accountToCount === 'all') {
        // Use shallow queries for ultra-fast counting (all accounts)
        countPromises = [
          get(ref(database, `/World/${userId}/inbox`)),
          get(ref(database, `/World/${userId}/spam`)),
          get(ref(database, `/World/${userId}/sentEmails`)),
          get(ref(database, `/World/${userId}/scheduledEmails`))
        ];
      } else {
        // 🎯 TARGETED: Count only emails from specific account
        console.log(`🎯 TARGETED COUNT: Counting only emails for account ${accountToCount}`);
        countPromises = [
          get(query(ref(database, `/World/${userId}/inbox`), orderByChild('accountId'), equalTo(accountToCount))),
          get(query(ref(database, `/World/${userId}/spam`), orderByChild('accountId'), equalTo(accountToCount))),
          get(ref(database, `/World/${userId}/sentEmails`)), // Keep sent emails count global for now
          get(ref(database, `/World/${userId}/scheduledEmails`)) // Keep scheduled emails count global for now
        ];
      }

      const [inboxSnapshot, spamSnapshot, sentSnapshot, scheduledSnapshot] = await Promise.all(countPromises);

      let totalEmails = 0, unreadCount = 0, spamCount = 0, deletedCount = 0;

      // 🚀 OPTIMIZED: Faster counting with for-loops
      if (inboxSnapshot.exists()) {
        const inboxData = inboxSnapshot.val();
        const entries = Object.values(inboxData);
        for (let i = 0; i < entries.length; i++) {
          const email = entries[i];
          if (email.deleted !== true) {
            totalEmails++;
            if (!email.isRead) unreadCount++;
          } else {
            deletedCount++;
          }
        }
      }

      if (spamSnapshot.exists()) {
        const spamData = spamSnapshot.val();
        const entries = Object.values(spamData);
        for (let i = 0; i < entries.length; i++) {
          const email = entries[i];
          if (email.deleted !== true) {
            spamCount++;
            totalEmails++;
          } else {
            deletedCount++;
          }
        }
      }

      const sentCount = sentSnapshot.exists() ? Object.keys(sentSnapshot.val()).length : 0;
      const scheduledCount = scheduledSnapshot.exists() ? Object.keys(scheduledSnapshot.val()).length : 0;

      setStats({
        total: totalEmails,
        unread: unreadCount,
        spam: spamCount,
        sent: sentCount,
        scheduled: scheduledCount,
        replied: 0,
        deleted: deletedCount
      });

      console.log(`🚀 TURBO-LOADED counts for ${accountToCount}: Total: ${totalEmails}, Unread: ${unreadCount}, Spam: ${spamCount}`);
      console.log(`📊 STATS DEBUG: Setting stats - Total: ${totalEmails}, Unread: ${unreadCount}, Spam: ${spamCount}`);
    } catch (error) {
      console.error('Error loading email counts:', error);
    }
  }, [userId, selectedAccount]);

  // Load email accounts
  useEffect(() => {
    if (!userId) return;

    const emailAccountsRef = ref(database, `/World/${userId}/settings/senderEmails`);
    
    const unsubscribe = onValue(emailAccountsRef, (snapshot) => {
      if (snapshot.exists()) {
        const accounts = [];
        snapshot.forEach((childSnapshot) => {
          accounts.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setEmailAccounts(accounts);
        console.log(`${accounts.length} email accounts loaded`);
      } else {
        setEmailAccounts([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Load sent emails (optimized - only for display, efficient count)
  useEffect(() => {
    if (!userId) return;

    console.log('Loading sent emails (optimized)...');
    
    // Load only recent sent emails for display (limit 20)
    const sentEmailsRef = query(
      ref(database, `/World/${userId}/sentEmails`),
      orderByChild('timestamp'),
      limitToFirst(20) // Only recent ones for display
    );

    const unsubscribeSent = onValue(sentEmailsRef, (snapshot) => {
      if (snapshot.exists()) {
        const sentEmailsData = snapshot.val();
        
        const sentEmailsArray = Object.entries(sentEmailsData).map(([id, email]) => ({
          id,
          ...email,
          isSent: true,
          date: email.date,
          from: email.from,
          subject: email.subject,
          bodyHtml: email.content,
          bodyText: email.text || '',
          isRead: true
        })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        
        setSentEmails(sentEmailsArray);
        console.log(`${sentEmailsArray.length} recent sent emails loaded for display`);
      } else {
        setSentEmails([]);
      }
    });
    
    return () => {
      unsubscribeSent();
    };
  }, [userId]);

  // Load forwarded emails (optimized - only recent ones)
  useEffect(() => {
    if (!userId) return;

    console.log('Loading forwarded emails (optimized)...');
    
    const forwardedEmailsRef = query(
      ref(database, `/World/${userId}/forwardedEmails`),
      orderByChild('timestamp'),
      limitToFirst(20) // Only recent ones
    );

    const unsubscribeForwarded = onValue(forwardedEmailsRef, (snapshot) => {
      if (snapshot.exists()) {
        const forwardedEmailsData = snapshot.val();
        
        const forwardedEmailsArray = Object.entries(forwardedEmailsData).map(([id, email]) => ({
          id,
          ...email,
          isForwarded: true,
          date: email.date,
          from: email.from,
          subject: email.subject,
          bodyHtml: email.content,
          bodyText: email.text || '',
          isRead: true
        })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        
        setForwardedEmails(forwardedEmailsArray);
        console.log(`${forwardedEmailsArray.length} recent forwarded emails loaded`);
      } else {
        setForwardedEmails([]);
      }
    });
    
    return () => {
      unsubscribeForwarded();
    };
  }, [userId]);

  // Initialize data loading
  useEffect(() => {
    if (!userId) return;

    // Load counts first (very fast and cheap) - ONLY for 'all' on initial load
    loadEmailCounts('all');
    
    // Then load first page of emails - ONLY for 'all' on initial load  
    loadEmailsPage(1, null, false, 'all');

    // Load settings
    const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
    const autoProcessRef = ref(database, `/World/${userId}/settings/autoProcessEmails`);
    
    get(inboxActiveRef).then((snapshot) => {
      setInboxActive(snapshot.exists() ? snapshot.val() : false);
    }).catch((error) => {
      console.error('Error loading inboxActive status:', error);
    });

    get(autoProcessRef).then((snapshot) => {
      setAutoProcessEmails(snapshot.exists() ? snapshot.val() : false);
    }).catch((error) => {
      console.error('Error loading autoProcessEmails status:', error);
    });

  }, [userId]); // 🚨 FIXED: Only depend on userId, not selectedAccount

  // 🔄 NEW: Reload emails when account changes (for inbox accounts only)
  useEffect(() => {
    if (!userId) return;
    
    // Only reload if account actually changed
    if (selectedAccount !== previousAccount) {
      console.log(`🔄 ACCOUNT CHANGED: ${previousAccount} → ${selectedAccount}`);
      
      // Update previous account
      setPreviousAccount(selectedAccount);
      
      // Only reload for inbox-related accounts (not sent, deleted, etc.)
      if (selectedAccount !== 'sent' && 
          selectedAccount !== 'replied' && 
          selectedAccount !== 'forwarded' && 
          selectedAccount !== 'deleted' && 
          selectedAccount !== 'scheduled') {
        
        console.log(`🔄 RELOADING emails for account: ${selectedAccount}`);
        
        // Load counts first (very fast and cheap)
        loadEmailCounts(selectedAccount);
        
        // Load first page for selected account
        loadEmailsPage(1, null, false, selectedAccount);
      }
    }
  }, [selectedAccount, userId, previousAccount]); // 🚨 FIXED: Now properly tracks actual changes

  // Lade AI Tags
  useEffect(() => {
    if (!userId) return;

    const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
    
    const unsubscribe = onValue(aiTagsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tagsData = snapshot.val();
        const tagsArray = Object.entries(tagsData).map(([id, tag]) => ({
          id,
          ...tag
        }));
        setAiTags(tagsArray);
        console.log(`${tagsArray.length} AI tags loaded`);
      } else {
        setAiTags([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Filtere replied emails aus sent emails
  useEffect(() => {
    const replied = sentEmails.filter(email => 
      email.emailType === 'custom_reply' || email.emailType === 'draft_reply'
    );
    setRepliedEmails(replied);
    console.log(`${replied.length} replied emails found from ${sentEmails.length} sent emails`);
  }, [sentEmails]);

  // Filtere E-Mails basierend auf dem ausgewählten Konto, Tag und Enthusiasmus-Filter
  useEffect(() => {
    let filtered = [];
    
    if (selectedAccount === 'sent') {
      filtered = sentEmails;
    } else if (selectedAccount === 'replied') {
      filtered = repliedEmails;
    } else if (selectedAccount === 'forwarded') {
      filtered = forwardedEmails;
    } else if (selectedAccount === 'deleted') {
      filtered = deletedEmails;
    } else if (selectedAccount === 'scheduled') {
      filtered = [];
    } else {
      // 🚀 OPTIMIZED: No need to filter by accountId anymore - Firebase does this for us!
      // For inbox accounts, emails are already filtered by Firebase queries
      filtered = emails;
      
      // Debug-Logging für Tag-Filterung
      if (selectedTag !== 'all') {
        console.log(`🏷️ [TAG-FILTER] Filtering for tag: ${selectedTag}`);
        console.log(`🏷️ [TAG-FILTER] Total emails before filtering: ${filtered.length}`);
        
        filtered = filtered.filter(email => {
          // Log email tag structure for debugging
          if (email.tags) {
            console.log(`🏷️ [TAG-FILTER] Email ${email.id} tag structure:`, {
              tags: email.tags
            });
          }
          
          // Prüfe, ob der Tag im tags-Objekt existiert
          const hasTag = email.tags && Object.values(email.tags).some(tag => tag.tag === selectedTag);
          
          if (hasTag) {
            console.log(`🏷️ [TAG-FILTER] Found matching email: ${email.id}`);
          }
          
          return hasTag;
        });
        
        console.log(`🏷️ [TAG-FILTER] Emails after filtering: ${filtered.length}`);
      }
      
      // Dann nach Enthusiasmus filtern
      if (enthusiasmFilter === 'enthusiastic') {
        filtered = filtered.filter(email => {
          return email.enthusiasmScore !== undefined && email.enthusiasmScore >= 5;
        });
      }
    }
    
    setFilteredEmails(filtered);
    
    if (selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled') {
      const filteredUnreadCount = filtered.filter(email => !email.isRead).length;
      const filteredSpamCount = filtered.filter(email => email.isSpam).length;
      
      // 🐛 FIXED: Don't override total count when filtering - only update if we're filtering
      // The total count should come from loadEmailCounts(), not from filtered emails
      if (selectedTag !== 'all' || enthusiasmFilter === 'enthusiastic') {
        // Only override stats when actually filtering - this gives filtered view
        setStats(prev => ({
          ...prev,
          total: filtered.length,
          unread: filteredUnreadCount,
          spam: filteredSpamCount
        }));
             } else {
         // No filters active - only update unread/spam from currently loaded emails, keep original total
         setStats(prev => {
           console.log(`📊 STATS DEBUG: Keeping original total (${prev.total}), updating unread/spam from ${filtered.length} loaded emails`);
           return {
             ...prev,
             // Keep original total from loadEmailCounts()
             unread: filteredUnreadCount,
             spam: filteredSpamCount
           };
         });
       }
      
      setEnthusiasmStats(enthusiasmStatsCalculated);
    }
    
  }, [emails, sentEmails, repliedEmails, forwardedEmails, deletedEmails, selectedAccount, enthusiasmFilter, selectedTag]);

  // Funktion zum Aktualisieren des inboxActive-Status
  const handleToggleInboxActive = () => {
    if (!userId) return;

    const newStatus = !inboxActive;
    setInboxActive(newStatus);
    
    // Speichere den neuen Status in der Datenbank
    const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
    set(inboxActiveRef, newStatus)
      .then(() => {
        console.log(`Smart Inbox status set to ${newStatus ? 'active' : 'inactive'}`);
        if (newStatus) {
          // Wenn aktiviert, sofort nach neuen E-Mails suchen
          handleRefresh();
        }
      })
      .catch((error) => {
        console.error('Error saving inboxActive status:', error);
        setInboxActive(!newStatus); // Zurücksetzen bei Fehler
      });
  };

  // Funktion zum manuellen Abrufen neuer E-Mails
  const handleRefresh = async () => {
    if (!userId) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      console.log('Searching for email accounts...');
      
      // Hole die E-Mail-Konten des Benutzers
      const emailAccountsRef = ref(database, `/World/${userId}/settings/senderEmails`);
      const emailAccountsSnapshot = await get(emailAccountsRef);
      
      if (!emailAccountsSnapshot.exists()) {
        setError('No email accounts found. Please set up an email account first.');
        setRefreshing(false);
        return;
      }
      
      // Extrahiere die Konto-IDs
      const accountIds = Object.keys(emailAccountsSnapshot.val());
      console.log(`${accountIds.length} email accounts found`);
      
      // Wenn ein bestimmtes Konto ausgewählt ist, nur dieses aktualisieren
      const accountsToRefresh = selectedAccount === 'all' || selectedAccount === 'sent' 
        ? accountIds 
        : [selectedAccount];
      
      // Führe für jedes relevante Konto einen Abruf durch
      const fetchPromises = accountsToRefresh.map(async (accountId) => {
        try {
          console.log(`Fetching emails for account ${accountId}...`);
          // Parameter für den E-Mail-Abruf
          const result = await manualFetchEmails({ 
            accountId,
            forceCheck: true,
            includeSpam: true,
            resetSpamUID: false // Wichtig: NIE die Spam-UID zurücksetzen bei normalem Refresh
          });
          console.log('Manual fetch result:', result.data);
          return result.data;
        } catch (accountError) {
          console.error(`Error fetching emails for account ${accountId}:`, accountError);
          throw accountError;
        }
      });
      
      // Warte auf alle Abrufe
      const results = await Promise.all(fetchPromises);
      console.log('All fetches completed:', results);
      
      // Prüfe, ob neue E-Mails gefunden wurden
      const allNoNewEmails = results.every(result => 
        result.success && result.message && result.message.includes('No new emails')
      );
      
      // Zeige die Info-Nachricht an, wenn keine neuen E-Mails gefunden wurden
      if (allNoNewEmails) {
        console.log('All accounts returned "No new emails found"');
        setError('Info: No new emails found.');
        setTimeout(() => setError(null), 5000); // Blende die Info nach 5 Sekunden aus
      }
      
      setRefreshing(false);
    } catch (error) {
      console.error('Error fetching new emails:', error);
      
      // Prüfe, ob es sich um den speziellen "keine neuen E-Mails" Fehler handelt
      if (error.message && (
          error.message.includes('No new emails') || 
          error.message.includes('no new messages'))) {
        setError('Info: No new emails found.');
        setTimeout(() => setError(null), 5000); // Blende die Info nach 5 Sekunden aus
      } else {
        // Bei anderen Fehlern zeige die Fehlermeldung an
        setError(`Error fetching new emails: ${error.message}`);
      }
      
      setRefreshing(false);
    }
  };

  // Funktion zum Löschen einer E-Mail
  const handleDeleteEmail = (deletedEmail) => {
    console.log(`Email ${deletedEmail.id} deleted`);
    
    // Aktualisiere den lokalen Zustand (die tatsächliche Löschung in der Datenbank
    // erfolgt in der SmartInboxEmail-Komponente)
    setEmails(prevEmails => prevEmails.filter(email => email.id !== deletedEmail.id));
    
    // Aktualisiere die Statistiken
    setStats(prevStats => ({
      ...prevStats,
      total: prevStats.total - 1,
      unread: deletedEmail.isRead ? prevStats.unread : prevStats.unread - 1,
      spam: deletedEmail.isSpam ? prevStats.spam - 1 : prevStats.spam
    }));
  };

  // Funktion zum Wechseln des ausgewählten Kontos
  const handleAccountChange = (accountId) => {
    if (accountId === selectedAccount) return; // No change needed
    
    console.log(`🔄 ACCOUNT SWITCH: User selected ${accountId}`);
    
    // Clear current state
    setEmails([]);
    setLastEmailKey(null);
    setCurrentPage(1);
    setHasMoreEmails(true);
    setPrefetchedNextPage(null); // Clear any prefetched data
    
    // Update selected account - this will trigger the useEffect above
    setSelectedAccount(accountId);
  };

  // Funktion zum Ändern des Tag-Filters
  const handleTagChange = (tagValue) => {
    setSelectedTag(tagValue);
  };

  // Funktion zum Ändern des Enthusiasmus-Filters
  const handleEnthusiasmFilterChange = (filterValue) => {
    setEnthusiasmFilter(filterValue);
  };

  // Berechnet die Begeisterungsgrad-Statistiken für die E-Mails
  const calculateEnthusiasmStats = (emailList) => {
    if (!emailList || emailList.length === 0) {
      return { low: 0, medium: 0, high: 0, unknown: 0 };
    }

    let low = 0;
    let medium = 0;
    let high = 0;
    let unknown = 0;

    emailList.forEach(email => {
      if (email.enthusiasmScore === undefined) {
        unknown++;
      } else if (email.enthusiasmScore >= 1 && email.enthusiasmScore <= 3) {
        low++;
      } else if (email.enthusiasmScore >= 4 && email.enthusiasmScore <= 6) {
        medium++;
      } else if (email.enthusiasmScore >= 7 && email.enthusiasmScore <= 10) {
        high++;
      } else {
        unknown++;
      }
    });

    return {
      low,
      medium,
      high,
      unknown
    };
  };

  // Funktion zum Markieren einer E-Mail als beantwortet
  const handleMarkAsReplied = async (emailToMark) => {
    if (!userId || !emailToMark || !emailToMark.id) return;
    
    try {
      // 1. Aktualisiere den lokalen Zustand
      setEmails(prevEmails => 
        prevEmails.map(email => 
          email.id === emailToMark.id 
            ? { ...email, isReplied: true } 
            : email
        )
      );
      
      // 2. Aktualisiere den Status in der Datenbank
      const emailRef = ref(database, `/World/${userId}/${emailToMark.isSpam ? 'spam' : 'inbox'}/${emailToMark.id}`);
      await update(emailRef, { isReplied: true });
      
      // 3. Rufe die Cloud-Funktion auf, um den Status auf dem Mailserver zu aktualisieren
      const functions = getFunctions();
      const markEmailAsReplied = httpsCallable(functions, 'markEmailAsReplied');
      await markEmailAsReplied({ 
        emailId: emailToMark.id, 
        accountId: emailToMark.accountId || selectedAccount,
        isSpam: emailToMark.isSpam || false
      });
      
      // 4. Aktualisiere die Statistik
      setStats(prev => ({
        ...prev,
        replied: prev.replied + 1
      }));
    } catch (err) {
      console.error("Fehler beim Markieren als beantwortet:", err);
      setError(`Fehler beim Markieren als beantwortet: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Funktion zum manuellen Verarbeiten einer E-Mail mit Agents
  const handleProcessEmailWithAgents = async (emailId, isSpam = false) => {
    try {
      console.log(`🤖 [MANUAL-PROCESSING] Processing email ${emailId} with agents...`);
      const result = await processEmailWithAgents({ emailId, isSpam });
      console.log(`🤖 [MANUAL-PROCESSING] Result:`, result.data);
      
      if (result.data.success) {
        if (result.data.action === 'draft_created') {
          console.log(`📝 [DRAFT-CREATED] Draft ${result.data.draftId} created by agent ${result.data.agentUsed.name}`);
        } else if (result.data.action === 'classified_no_reply') {
          console.log(`🏷️ [CLASSIFIED] Email classified by agent ${result.data.agentUsed.name} - no reply needed`);
        }
      }
    } catch (error) {
      console.error(`🤖 [MANUAL-PROCESSING] Error processing email ${emailId}:`, error);
    }
  };

  // Funktion zum Öffnen des EmailAiPopup mit Draft-Daten
  const handleOpenDraftInEmailPopup = async (draft) => {
    console.log(`📝 [DRAFT-POPUP] Opening draft in EmailAiPopup:`, {
      draftId: draft.id,
      agentName: draft.agentName,
      originalEmailSubject: draft.originalEmail?.subject
    });

    // Detailliertes Logging der Draft-Daten
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Complete originalEmail object from draft:`, draft.originalEmail);
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Missing fields check:`, {
      hasBodyText: !!draft.originalEmail?.bodyText,
      hasContent: !!draft.originalEmail?.content,
      hasBody: !!draft.originalEmail?.body,
      hasText: !!draft.originalEmail?.text,
      allFields: Object.keys(draft.originalEmail || {})
    });

    // Versuche die vollständige E-Mail aus der Inbox zu laden
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Attempting to load full email data for ID: ${draft.originalEmailId}`);

    // Lade die vollständigen E-Mail-Daten, falls sie in den Draft-Daten fehlen
    let fullOriginalEmail = draft.originalEmail;
    
    // Prüfe, ob wichtige Felder fehlen (bodyText, content, etc.)
    const missingContent = !draft.originalEmail?.bodyText && 
                          !draft.originalEmail?.content && 
                          !draft.originalEmail?.body;
    
    if (missingContent && draft.originalEmailId) {
      console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Content missing, loading full email from database...`);
      
      // Versuche zuerst aus der Inbox zu laden
      let loadedEmail = await loadFullEmailData(draft.originalEmailId, false);
      
      // Falls nicht in Inbox gefunden, versuche Spam-Ordner
      if (!loadedEmail && draft.originalEmailPath === 'spam') {
        console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Not found in inbox, trying spam folder...`);
        loadedEmail = await loadFullEmailData(draft.originalEmailId, true);
      }
      
      if (loadedEmail) {
        console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Successfully loaded full email data`);
        fullOriginalEmail = {
          ...draft.originalEmail,
          ...loadedEmail,
          // Stelle sicher, dass die wichtigsten Felder gesetzt sind
          bodyText: loadedEmail.bodyText || loadedEmail.content || loadedEmail.body || "",
          content: loadedEmail.content || loadedEmail.bodyText || loadedEmail.body || "",
          body: loadedEmail.body || loadedEmail.bodyText || loadedEmail.content || ""
        };
      } else {
        console.warn(`📝 [DRAFT-ORIGINAL-EMAIL] Could not load full email data for ${draft.originalEmailId}`);
      }
    }

    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Final originalEmail object:`, {
      hasBodyText: !!fullOriginalEmail?.bodyText,
      hasContent: !!fullOriginalEmail?.content,
      hasBody: !!fullOriginalEmail?.body,
      contentLength: (fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "").length
    });

    // Erstelle ein Objekt mit den Daten, die EmailAiPopup benötigt
    const emailReplyData = {
      id: draft.originalEmailId, // Verwende die ursprüngliche E-Mail-ID
      business_name: extractSenderName(fullOriginalEmail?.from),
      lead_email: extractEmailFromString(fullOriginalEmail?.from),
      lead_description: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_reply",
      // Speichere die ursprüngliche E-Mail für den Kontext (für normale E-Mail-Antworten)
      original_email: {
        subject: fullOriginalEmail?.subject,
        content: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
        bodyText: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
        body: fullOriginalEmail?.body || fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "",
        from: fullOriginalEmail?.from,
        date: fullOriginalEmail?.date,
        timestamp: fullOriginalEmail?.date
      },
      // Füge die Draft-Informationen hinzu - WICHTIG: originalEmail muss hier auch gespeichert werden
      draft: {
        id: draft.id,
        content: draft.draftContent,
        agentName: draft.agentName,
        agentId: draft.agentId,
        agentCaseNumber: draft.agentCaseNumber,
        agentTask: draft.agentTask,
        status: draft.status,
        createdAt: draft.createdAt,
        // Füge die originalEmail auch hier hinzu, damit InboxModeEmail.js sie findet
        originalEmail: {
          subject: fullOriginalEmail?.subject,
          content: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
          bodyText: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
          body: fullOriginalEmail?.body || fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "",
          from: fullOriginalEmail?.from,
          date: fullOriginalEmail?.date,
          timestamp: fullOriginalEmail?.date
        }
      },
      // Konto-Informationen (falls verfügbar)
      accountEmail: fullOriginalEmail?.accountEmail,
      accountId: fullOriginalEmail?.accountId
    };

    // Detailliertes Logging der übergebenen Daten
    console.log(`📝 [DRAFT-DATA] Complete emailReplyData structure:`, emailReplyData);
    console.log(`📝 [DRAFT-DATA] original_email:`, emailReplyData.original_email);
    console.log(`📝 [DRAFT-DATA] draft.originalEmail:`, emailReplyData.draft.originalEmail);
    console.log(`📝 [DRAFT-DATA] Raw draft object from database:`, draft);

    setDraftEmailData(emailReplyData);
    setShowDraftEmailPopup(true);
  };

  // Hilfsfunktion zum Extrahieren des Sender-Namens
  const extractSenderName = (fromEmail) => {
    if (!fromEmail) return 'Unknown';
    
    // Extrahiere Name aus "Name <email@domain.com>" Format
    const nameMatch = fromEmail.match(/^([^<]+)</);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    
    // Falls nur E-Mail-Adresse, extrahiere den Teil vor @
    const emailMatch = fromEmail.match(/([^@]+)@/);
    return emailMatch ? emailMatch[1] : fromEmail;
  };

  // Hilfsfunktion zum Extrahieren der E-Mail-Adresse
  const extractEmailFromString = (fromString) => {
    if (!fromString) return '';
    
    // Extrahiere E-Mail aus "Name <email@domain.com>" Format
    const emailMatch = fromString.match(/<([^>]+)>/);
    if (emailMatch) {
      return emailMatch[1].trim();
    }
    
    // Falls es nur eine E-Mail-Adresse ist
    if (fromString.includes('@')) {
      return fromString.trim();
    }
    
    return '';
  };

  // Callback für das Schließen des Draft EmailAiPopup
  const handleDraftEmailPopupClose = (emailSent = false) => {
    setShowDraftEmailPopup(false);
    
    if (emailSent) {
      console.log(`📝 [DRAFT-POPUP] Email sent successfully from draft`);
      
      // Entferne den Draft sofort aus der EmailDraftsPreview (optimistische UI-Aktualisierung)
      if (draftEmailData && draftEmailData.draft && window.emailDraftsPreview && window.emailDraftsPreview.markDraftAsSending) {
        window.emailDraftsPreview.markDraftAsSending(draftEmailData.draft.id);
      }
    }
    
    setDraftEmailData(null);
  };

  // Hilfsfunktion zum Laden der vollständigen E-Mail-Daten
  const loadFullEmailData = async (emailId, isSpam = false) => {
    try {
      console.log(`📧 [LOAD-FULL-EMAIL] Loading full email data for ID: ${emailId}, isSpam: ${isSpam}`);
      
      const emailPath = isSpam ? 'spam' : 'inbox';
      const emailRef = ref(database, `/World/${userId}/${emailPath}/${emailId}`);
      const snapshot = await get(emailRef);
      
      if (snapshot.exists()) {
        const fullEmailData = snapshot.val();
        console.log(`📧 [LOAD-FULL-EMAIL] Successfully loaded full email data:`, {
          hasBodyText: !!fullEmailData.bodyText,
          hasBodyHtml: !!fullEmailData.bodyHtml,
          hasContent: !!fullEmailData.content,
          subject: fullEmailData.subject
        });
        return fullEmailData;
      } else {
        console.warn(`📧 [LOAD-FULL-EMAIL] Email ${emailId} not found in ${emailPath}`);
        return null;
      }
    } catch (error) {
      console.error(`📧 [LOAD-FULL-EMAIL] Error loading email ${emailId}:`, error);
      return null;
    }
  };

  // Handler für Draft Bulk Mode
  const handleDraftBulkMode = (drafts, selectedIndex = 0) => {
    setBulkDrafts(drafts);
    setSelectedDraftIndex(selectedIndex);
    setShowDraftBulkMode(true);
  };

  // Callback für das Schließen des Draft Bulk Mode
  const handleDraftBulkModeClose = () => {
    setShowDraftBulkMode(false);
    setBulkDrafts([]);
  };

  // Lade User Settings, AI Settings, Topics und Sender Emails für den Bulk Mode
  useEffect(() => {
    if (!userId) return;

    // Lade User Settings
    const userSettingsRef = ref(database, `/World/${userId}/settings`);
    const unsubscribeUserSettings = onValue(userSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserSettings(snapshot.val());
      }
    });

    // Lade AI Settings
    const aiSettingsRef = ref(database, `/World/${userId}/aiSettings`);
    const unsubscribeAiSettings = onValue(aiSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAiSettings(snapshot.val());
      }
    });

    // Lade User Topics
    const userTopicsRef = ref(database, `/World/${userId}/topics`);
    const unsubscribeUserTopics = onValue(userTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTopics(snapshot.val());
        setIsTopicsLoaded(true);
      } else {
        setUserTopics({});
        setIsTopicsLoaded(true);
      }
    });

    // Lade Sender Emails
    const senderEmailsRef = ref(database, `/World/${userId}/settings/senderEmails`);
    const unsubscribeSenderEmails = onValue(senderEmailsRef, (snapshot) => {
      if (snapshot.exists()) {
        const emails = [];
        snapshot.forEach((childSnapshot) => {
          emails.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setSenderEmails(emails);
      } else {
        setSenderEmails([]);
      }
    });

    // Topic Type Mapping
    setTopicTypeMapping({
      "Introduce Yourself": "intro",
      "Follow Up": "follow_up_1", 
      "Second Follow Up": "follow_up_2",
      "Third Follow Up": "follow_up_3",
      "Reply To Mail": "custom_reply"
    });

    // Lade Footer Settings separat und füge sie zu den userSettings hinzu
    const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
    const unsubscribeFooterSettings = onValue(footerSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const footerData = snapshot.val();
        console.log(`🦶 [SMARTINBOX] Footer settings loaded:`, footerData);
        
        // Füge Footer-Daten zu den userSettings hinzu
        setUserSettings(prev => ({
          ...prev,
          footer_text: footerData.footer_text,
          footer_image_url: footerData.footer_image_url,
          footer_image_position: footerData.footer_image_position,
          footer_font: footerData.footer_font,
          footer_image_size: footerData.footer_image_size
        }));
      } else {
        console.log(`🦶 [SMARTINBOX] No footer settings found`);
      }
    });

    return () => {
      unsubscribeUserSettings();
      unsubscribeAiSettings();
      unsubscribeUserTopics();
      unsubscribeSenderEmails();
      unsubscribeFooterSettings();
    };
  }, [userId]);

  // Zähle gelöschte E-Mails für Statistiken (nur einmal beim Start)
  useEffect(() => {
    if (!userId) return;

    const countDeletedEmails = async () => {
      try {
        // Zähle gelöschte E-Mails aus /inbox
        const inboxEmailsRef = ref(database, `/World/${userId}/inbox`);
        const inboxSnapshot = await get(inboxEmailsRef);
        
        // Zähle gelöschte E-Mails aus /spam
        const spamEmailsRef = ref(database, `/World/${userId}/spam`);
        const spamSnapshot = await get(spamEmailsRef);
        
        let deletedCount = 0;
        
        // Zähle gelöschte E-Mails aus /inbox
        if (inboxSnapshot.exists()) {
          const inboxData = inboxSnapshot.val();
          Object.values(inboxData).forEach(email => {
            if (email.deleted === true) {
              deletedCount++;
            }
          });
        }
        
        // Zähle gelöschte E-Mails aus /spam
        if (spamSnapshot.exists()) {
          const spamData = spamSnapshot.val();
          Object.values(spamData).forEach(email => {
            if (email.deleted === true) {
              deletedCount++;
            }
          });
        }
        
        // Aktualisiere nur die Statistik
        setStats(prev => ({
          ...prev,
          deleted: deletedCount
        }));
        
        console.log(`${deletedCount} deleted emails found for statistics`);
      } catch (error) {
        console.error('Error counting deleted emails:', error);
        setStats(prev => ({
          ...prev,
          deleted: 0
        }));
      }
    };
    
    // Einmaliges Zählen beim Start
    countDeletedEmails();
  }, [userId]);

  // Load deleted emails on demand (only when deleted filter is selected)
  const loadDeletedEmailsOnDemand = async () => {
    if (!userId) return;

    console.log('Loading deleted emails on demand...');
    
    try {
      // Load deleted emails efficiently with limit
      const inboxQuery = query(
        ref(database, `/World/${userId}/inbox`),
        orderByChild('deleted'),
        limitToFirst(50) // Limit deleted emails
      );
      
      const spamQuery = query(
        ref(database, `/World/${userId}/spam`),
        orderByChild('deleted'),
        limitToFirst(50) // Limit deleted emails
      );
      
      const [inboxSnapshot, spamSnapshot] = await Promise.all([
        get(inboxQuery),
        get(spamQuery)
      ]);
      
      let deletedEmailsArray = [];
      
      // Process deleted emails from inbox
      if (inboxSnapshot.exists()) {
        const inboxData = inboxSnapshot.val();
        Object.entries(inboxData).forEach(([id, email]) => {
          if (email.deleted === true) {
            deletedEmailsArray.push({
              id,
              ...email,
              isDeleted: true,
              deletionSource: 'inbox'
            });
          }
        });
      }
      
      // Process deleted emails from spam
      if (spamSnapshot.exists()) {
        const spamData = spamSnapshot.val();
        Object.entries(spamData).forEach(([id, email]) => {
          if (email.deleted === true) {
            deletedEmailsArray.push({
              id,
              ...email,
              isDeleted: true,
              isSpam: true,
              deletionSource: 'spam'
            });
          }
        });
      }
      
      // Sort by deletion time (newest first)
      deletedEmailsArray.sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
      
      setDeletedEmails(deletedEmailsArray);
      
      console.log(`${deletedEmailsArray.length} deleted emails loaded on demand`);
    } catch (error) {
      console.error('Error loading deleted emails:', error);
      setDeletedEmails([]);
    }
  };

  // Load deleted emails only when the "deleted" filter is selected
  useEffect(() => {
    if (selectedAccount === 'deleted' && deletedEmails.length === 0) {
      loadDeletedEmailsOnDemand();
    }
  }, [selectedAccount]);

  // Function to analyze an email
  const handleAnalyzeEmail = async (emailId, isSpam = false) => {
    try {
      console.log(`🔍 [ANALYZE] Analyzing email ${emailId} (Spam: ${isSpam})...`);
      const result = await analyzeEmailEndpoint({ emailId, isSpam });
      console.log(`🔍 [ANALYZE] Analysis result:`, result.data);
      
      // Update the email in local state with new tags
      if (result.data && result.data.aiTags) {
        setEmails(prevEmails => prevEmails.map(email => {
          if (email.id === emailId) {
            return {
              ...email,
              aiTags: result.data.aiTags,
              analysis: {
                ...email.analysis,
                aiTags: result.data.aiTags
              }
            };
          }
          return email;
        }));
      }
      
    } catch (error) {
      console.error(`❌ [ANALYZE] Error analyzing email ${emailId}:`, error);
    }
  };

  return (
    <div className="smartInbox">
      <div className="smartInbox_header">
        <div className="smartInbox_header_left">
          <h2>Inbox</h2>
        </div>
        <div className="smartInbox_stats">
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.total}</span>
            <span className="smartInbox_stats_label">Total</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.unread}</span>
            <span className="smartInbox_stats_label">Unread</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.spam}</span>
            <span className="smartInbox_stats_label">Spam</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.scheduled}</span>
            <span className="smartInbox_stats_label">Scheduled</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.sent}</span>
            <span className="smartInbox_stats_label">Sent</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.deleted}</span>
            <span className="smartInbox_stats_label">Deleted</span>
          </div>
        </div>
        <div className="smartInbox_buttons">
          {/* Toggle Switches */}
          <div className="smartInbox_header_toggles">
            {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && stats.total > 0 && (
              <div className="smartInbox_header_toggle">
                <span className="toggle_label">Analytics</span>
                <div 
                  className={`smartInbox_toggle_switch ${showEnthusiasmDistribution ? 'active' : ''}`}
                  onClick={() => setShowEnthusiasmDistribution(v => !v)}
                >
                  <div className="smartInbox_toggle_slider"></div>
                </div>
              </div>
            )}
            
            {/* Filters Toggle - immer anzeigen wenn E-Mails vorhanden sind oder wenn scheduled/sent/replied/forwarded/deleted ausgewählt ist */}
            {(filteredEmails.length > 0 || selectedAccount === 'sent' || selectedAccount === 'replied' || selectedAccount === 'forwarded' || selectedAccount === 'deleted' || selectedAccount === 'scheduled') && (
              <div className="smartInbox_header_toggle">
                <span className="toggle_label">Filters</span>
                <div 
                  className={`smartInbox_toggle_switch ${showMoreFilters ? 'active' : ''}`}
                  onClick={() => setShowMoreFilters(v => !v)}
                >
                  <div className="smartInbox_toggle_slider"></div>
                </div>
              </div>
            )}
          </div>
          
          <button 
            className={`smartInbox_button ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            style={{backgroundColor: 'transparent', color: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'}}
          >
            {!refreshing && <img style={{width: '14px', marginRight: '5px'}} src="/img/refresh-arrow.svg" alt="Refresh" />}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Begeisterungsgrad-Verteilung - nur anzeigen, wenn nicht "Sent Emails", "Replied Emails", "Deleted Emails" oder "Scheduled Emails" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && stats.total > 0 && showEnthusiasmDistribution ? (
        <div className="smartInbox_enthusiasm_distribution">
          <div className="smartInbox_enthusiasm_label">Enthusiasm Distribution:</div>
          <div className="smartInbox_enthusiasm_bar">
            {enthusiasmStats.low > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_low" 
                style={{ width: `${(enthusiasmStats.low / stats.total) * 100}%` }}
                title={`Low (1-3): ${enthusiasmStats.low} emails (${Math.round((enthusiasmStats.low / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.low > stats.total * 0.05 && `${Math.round((enthusiasmStats.low / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.medium > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_medium" 
                style={{ width: `${(enthusiasmStats.medium / stats.total) * 100}%` }}
                title={`Medium (4-6): ${enthusiasmStats.medium} emails (${Math.round((enthusiasmStats.medium / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.medium > stats.total * 0.05 && `${Math.round((enthusiasmStats.medium / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.high > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_high" 
                style={{ width: `${(enthusiasmStats.high / stats.total) * 100}%` }}
                title={`High (7-10): ${enthusiasmStats.high} emails (${Math.round((enthusiasmStats.high / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.high > stats.total * 0.05 && `${Math.round((enthusiasmStats.high / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.unknown > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_unknown" 
                style={{ width: `${(enthusiasmStats.unknown / stats.total) * 100}%` }}
                title={`Unknown: ${enthusiasmStats.unknown} emails (${Math.round((enthusiasmStats.unknown / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.unknown > stats.total * 0.05 && `${Math.round((enthusiasmStats.unknown / stats.total) * 100)}%`}
              </div>
            )}
          </div>
          <div className="smartInbox_enthusiasm_legend">
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_low"></span>
              <span className="smartInbox_enthusiasm_legend_text">Low (1-3)</span>
            </div>
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_medium"></span>
              <span className="smartInbox_enthusiasm_legend_text">Medium (4-6)</span>
            </div>
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_high"></span>
              <span className="smartInbox_enthusiasm_legend_text">High (7-10)</span>
            </div>
            {enthusiasmStats.unknown > 0 && (
              <div className="smartInbox_enthusiasm_legend_item">
                <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_unknown"></span>
                <span className="smartInbox_enthusiasm_legend_text">Unknown</span>
              </div>
            )}
          </div>
        </div>
      ) : selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && stats.total > 0 && !showEnthusiasmDistribution ? null : selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && stats.total > 0 ? (
        <div className="smartInbox_feature_locked">
          <div className="smartInbox_lock_icon">
            <span className="smartInbox_lock_circle">
              <span className="smartInbox_lock_symbol">
                <img style={{width: '28px', marginTop: '5px', filter: 'invert(1)'}} src="/img/lock.svg" alt="Lock" />
              </span>
            </span>
          </div>
          <h3 className="smartInbox_locked_title">Analytics Feature Locked</h3>
          <p className="smartInbox_locked_message">
            Generate at least 100 Replies to show this feature
          </p>
          <div className="smartInbox_progress_container">
            <div className="smartInbox_progress_bar">
              <div 
                className="smartInbox_progress_fill" 
                style={{ width: `${Math.min(stats.total / 100 * 100, 100)}%` }}
              ></div>
            </div>
            <div className="smartInbox_progress_text">
              {stats.total}/100 Emails
            </div>
          </div>
        </div>
      ) : null}

      {/* Tag Distribution - nur anzeigen wenn nicht "Sent Emails", "Deleted Emails" oder "Scheduled Emails" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && stats.total > 0 && showEnthusiasmDistribution && aiTags.length > 0 ? (
        <div className="smartInbox_enthusiasm_distribution">
          <div className="smartInbox_tag_distribution">
            <div className="smartInbox_enthusiasm_label">Tag Distribution:</div>
            <div className="smartInbox_enthusiasm_bar">
              {aiTags.map(tag => {
                // Zähle E-Mails mit diesem Tag
                const emailsWithTag = emails.filter(email => 
                  email.tags && Object.values(email.tags).some(t => t.tag === tag.tag)
                ).length;
                
                // Berechne den Prozentsatz
                const percentage = (emailsWithTag / stats.total) * 100;
                
                // Nur anzeigen wenn es E-Mails mit diesem Tag gibt
                return emailsWithTag > 0 ? (
                  <div 
                    key={tag.id}
                    className="smartInbox_enthusiasm_segment" 
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: tag.color || '#4a90e2'
                    }}
                    title={`${tag.name}: ${emailsWithTag} emails (${Math.round(percentage)}%)`}
                  >
                    {percentage > 5 && `${Math.round(percentage)}%`}
                  </div>
                ) : null;
              })}
            </div>
            <div className="smartInbox_enthusiasm_legend">
              {aiTags.map(tag => {
                // Zähle E-Mails mit diesem Tag
                const emailsWithTag = emails.filter(email => 
                  email.tags && Object.values(email.tags).some(t => t.tag === tag.tag)
                ).length;
                
                // Nur in der Legende anzeigen wenn es E-Mails mit diesem Tag gibt
                return emailsWithTag > 0 ? (
                  <div key={tag.id} className="smartInbox_enthusiasm_legend_item">
                    <span 
                      className="smartInbox_enthusiasm_legend_color" 
                      style={{ backgroundColor: tag.color || '#4a90e2' }}
                    ></span>
                    <span className="smartInbox_enthusiasm_legend_text">
                      {tag.name} ({emailsWithTag})
                    </span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Inbox Filters Component */}
      <InboxFilters 
        selectedAccount={selectedAccount}
        onAccountChange={handleAccountChange}
        emailAccounts={emailAccounts}
        formatEmailAddress={formatEmailAddress}
        showMoreFilters={showMoreFilters}
        selectedTag={selectedTag}
        onTagChange={handleTagChange}
        aiTags={aiTags}
        emails={emails}
        enthusiasmFilter={enthusiasmFilter}
        onEnthusiasmFilterChange={handleEnthusiasmFilterChange}
      />

      {/* Email Drafts Preview - nur anzeigen, wenn nicht "Sent Emails", "Replied Emails", "Forwarded Emails", "Deleted Emails" oder "Scheduled Emails" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && (
        <EmailDraftsPreview 
          userId={userId} 
          onOpenDraftInEmailPopup={handleOpenDraftInEmailPopup}
          onOpenDraftBulkMode={handleDraftBulkMode}
        />
      )}

      {error && error.includes('Info:') ? (
        <div className="smartInbox_info_message">
          <p>{error.replace('Info: ', '')}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      ) : error ? (
        <div className="smartInbox_error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      ) : null}

      {loading ? (
        <div className="smartInbox_loading">
          <img src="/img/salesgod_dark.webp" alt="SalesGod" className="smartInbox_spinner" />
          <p>Loading emails...</p>
        </div>
      ) : filteredEmails.length === 0 && selectedAccount !== 'scheduled' ? (
        <div className="smartInbox_empty">
          <div className="smartInbox_empty_illustration">
            <img 
              src="/img/mailbox-empty.svg" 
              alt="Empty Inbox" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/img/mailerle.webp";  // Fallback auf existierendes Bild
              }}
              style={{ maxWidth: "80px", maxHeight: "80px" }}
            />
          </div>
          <h3 className="smartInbox_empty_title">
            {selectedAccount === 'sent'
              ? "No sent emails found"
              : selectedAccount === 'replied'
                ? "No replied emails found"
                : selectedAccount === 'forwarded'
                  ? "No forwarded emails found"
                  : selectedAccount === 'deleted'
                    ? "No deleted emails found"
                    : selectedAccount === 'all'
                      ? "No emails found"
                      : `No emails found for this account`}
          </h3>
          
          {/* Nur Smart Inbox Aktivierung anzeigen, wenn noch keine E-Mails in der DB sind */}
          {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && emails.length === 0 && (
            <div className="smartInbox_activation">
              <div className="smartInbox_toggle_container">
                <div className="smartInbox_toggle_header">
                  <h4 className="smartInbox_toggle_title">Smart Inbox</h4>
                  <div className={`smartInbox_toggle ${inboxActive ? 'active' : ''}`} onClick={handleToggleInboxActive}>
                    <div className="smartInbox_toggle_switch_2"></div>
                  </div>
                </div>
                <p className="smartInbox_toggle_description">
                  {inboxActive 
                    ? "Smart Inbox is active - emails are being retrieved automatically" 
                    : "Step 1: Activate Smart Inbox to enable automatic email retrieval"}
                </p>
                <p className="smartInbox_privacy_note">
                  {inboxActive 
                    ? "Your email data is securely stored on BonusClub's servers to power smart features."
                    : "By activating Smart Inbox, you agree that your email data will be securely stored on BonusClub's servers to enable our smart features and improve your experience."}
                </p>
              </div>
            </div>
          )}
          
          <button 
            className="smartInbox_refresh_button" 
            onClick={handleRefresh} 
            disabled={refreshing || (emails.length === 0 && !inboxActive)}
          >
            {refreshing ? (
              <>
                <span className="refresh_spinner"></span>
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 8.00001C14 11.3137 11.3137 14 8.00001 14C4.68629 14 2.00001 11.3137 2.00001 8.00001C2.00001 4.68629 4.68629 2.00001 8.00001 2.00001C9.88652 2.00001 11.5695 2.90844 12.6003 4.27446" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M14 3.50001V8.00001H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{emails.length === 0 && !inboxActive ? "Activate Smart Inbox First" : "Refresh Inbox"}</span>
              </>
            )}
          </button>
        </div>
      ) : selectedAccount === 'scheduled' ? (
        <ScheduledEmails userId={userId} />
      ) : (
        <div>
          {/* Titel für spezielle E-Mail-Ansichten */}
          {(selectedAccount === 'sent' || selectedAccount === 'replied' || selectedAccount === 'forwarded' || selectedAccount === 'deleted' || selectedAccount === 'scheduled') && (
            <div className={`smartInbox_section_header smartInbox_section_header_${selectedAccount}`}>
              <h3 className="smartInbox_section_title">
                {selectedAccount === 'sent' && (
                  <>
                    Sent Emails
                    <div className="smartInbox_section_count smartInbox_section_count_sent">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'replied' && (
                  <>
                    Replied Emails
                    <div className="smartInbox_section_count smartInbox_section_count_replied">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'forwarded' && (
                  <>
                    Forwarded Emails
                    <div className="smartInbox_section_count smartInbox_section_count_forwarded">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'deleted' && (
                  <>
                    Deleted Emails
                    <div className="smartInbox_section_count smartInbox_section_count_deleted">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'scheduled' && 'Scheduled Emails'}
              </h3>
            </div>
          )}
          
          <div className="smartInbox_emails">
            {filteredEmails.map((email) => (
              <SmartInboxEmail
                key={email.id}
                email={email}
                userId={userId}
                emailType={selectedAccount === 'sent' ? 'sent' : selectedAccount === 'replied' ? 'replied' : selectedAccount === 'forwarded' ? 'forwarded' : selectedAccount === 'deleted' ? 'deleted' : 'inbox'}
                onDelete={handleDeleteEmail}
                onMarkAsReplied={handleMarkAsReplied}
                onProcessWithAgents={handleProcessEmailWithAgents}
                autoProcessEnabled={aiSettings.autoProcessEmails}
              />
            ))}
            
            {/* NEW: Load More Button for Pagination */}
            {(selectedAccount === 'all' || (selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled')) && hasMoreEmails && (
              <div className="smartInbox_load_more_container">
                <button 
                  className="smartInbox_load_more_button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="load_more_spinner"></span>
                      <span>Loading more emails...</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 4v8m-4-4h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>Load More ({filteredEmails.length} of {stats.total})</span>
                    </>
                  )}
                </button>
                <p className="smartInbox_pagination_info">
                  Showing {filteredEmails.length} emails • Page {currentPage} • Account: {selectedAccount === 'all' ? 'All Inboxes' : (emailAccounts.find(acc => acc.id === selectedAccount)?.email || selectedAccount)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Draft EmailAiPopup über Portal rendern */}
      {showDraftEmailPopup && draftEmailData && (
        <div className="popupOverlayWrapper" style={{ zIndex: 10000 }}>
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <EmailAiPopup 
              onClose={handleDraftEmailPopupClose} 
              rowData={draftEmailData} 
              userId={userId}
              isReply={true}
              source="draft"
              onEmailResult={(status, message) => {
                console.log(`📝 [DRAFT-POPUP] Email result: ${status} - ${message}`);
                if (status === 'success') {
                  handleDraftEmailPopupClose(true);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Draft Bulk Mode über Portal rendern */}
      {showDraftBulkMode && bulkDrafts.length > 0 && (
        <div className="popupOverlayWrapper" style={{ zIndex: 10000 }}>
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <DraftBulkMode 
              onClose={handleDraftBulkModeClose}
              userId={userId}
              onEmailResult={(status, message) => {
                console.log(`📝 [DRAFT-BULK] Email result: ${status} - ${message}`);
              }}
              userSettings={userSettings}
              aiSettings={aiSettings}
              userTopics={userTopics}
              isTopicsLoaded={isTopicsLoaded}
              senderEmails={senderEmails}
              topicTypeMapping={topicTypeMapping}
              bulkDrafts={bulkDrafts}
              initialDraftIndex={selectedDraftIndex}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartInbox;